<script setup>
import PageSpeedActionsCell from "@frontJs/components/tables/cells/projectManagement/dev/PageSpeedActionsCell.vue";
import {
  PlusIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { tenant } = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "url",
      header: () => t("label.url"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.url,
          href: props.row.original.url,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "devPageSpeedMetricsLast.desktopScore",
      header: () => t("label.desktopScore"),
      cell: (props) =>
        props.row.original.lastDesktopScore
          ? h(PillCell, {
              pillValue: props.row.original.lastDesktopScore,
              type:
                props.row.original.lastDesktopScore >= 90
                  ? "success"
                  : props.row.original.lastDesktopScore >= 50
                    ? "warning"
                    : "danger",
              leftIcon: ComputerDesktopIcon,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "devPageSpeedMetricsLast.desktopWebCoreVitalsScore",
      header: () => t("label.desktopWebCoreVitals"),
      cell: (props) =>
        props.row.original.lastDesktopWebCoreVitalsScore
          ? h(PillCell, {
              pillValue:
                props.row.original.lastDesktopWebCoreVitalsScore === "FAST"
                  ? t("label.fast")
                  : props.row.original.lastDesktopWebCoreVitalsScore ===
                      "AVERAGE"
                    ? t("label.medium")
                    : t("label.slow"),
              type:
                props.row.original.lastDesktopWebCoreVitalsScore === "FAST"
                  ? "success"
                  : props.row.original.lastDesktopWebCoreVitalsScore ===
                      "AVERAGE"
                    ? "warning"
                    : "danger",
              leftIcon: ComputerDesktopIcon,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "devPageSpeedMetricsLast.mobileScore",
      header: () => t("label.mobileScore"),
      cell: (props) =>
        props.row.original.lastMobileScore
          ? h(PillCell, {
              pillValue: props.row.original.lastMobileScore,
              type:
                props.row.original.lastMobileScore >= 90
                  ? "success"
                  : props.row.original.lastMobileScore >= 50
                    ? "warning"
                    : "danger",
              leftIcon: DevicePhoneMobileIcon,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "devPageSpeedMetricsLast.mobileWebCoreVitalsScore",
      header: () => t("label.mobileWebCoreVitals"),
      cell: (props) =>
        props.row.original.lastMobileWebCoreVitalsScore
          ? h(PillCell, {
              pillValue:
                props.row.original.lastMobileWebCoreVitalsScore === "FAST"
                  ? t("label.fast")
                  : props.row.original.lastMobileWebCoreVitalsScore ===
                      "AVERAGE"
                    ? t("label.medium")
                    : t("label.slow"),
              type:
                props.row.original.lastMobileWebCoreVitalsScore === "FAST"
                  ? "success"
                  : props.row.original.lastMobileWebCoreVitalsScore ===
                      "AVERAGE"
                    ? "warning"
                    : "danger",
              leftIcon: DevicePhoneMobileIcon,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "devPageSpeedMetricsLast.createdAt",
      header: () => t("label.checkedAt"),
      cell: (props) =>
        h(TextCellV2, {
          text: formatDate(props.row.original.lastVisitAt, false, true),
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(PageSpeedActionsCell, {
          tenant: tenant,
          row: innerProps.row,
          callback: updateData,
        }),
    }),
  ],
  url: route("front.projectManagement.dev.postPageSpeed", {
    tenant: tenant.id,
  }),
  columnPinning: {
    right: ['actions']
  }
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    >
      <template #actions>
        <ActionButton
          size="small"
          :href="
            route('front.projectManagement.dev.createPageSpeedUrl', {
              tenant,
            })
          "
          :left-icon="PlusIcon"
        >
          {{ t("button.add") }}
        </ActionButton>
      </template>
    </DataTableLayout>
  </div>
</template>
